import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { lazy, Suspense } from "react";
import CookieConsent from "./components/CookieConsent";
import PageTransition from "./components/PageTransition";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Projects = lazy(() => import("./pages/Projects"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Contact = lazy(() => import("./pages/Contact"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const BookConsultation = lazy(() => import("./pages/BookConsultation"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const ConsultationIntake = lazy(() => import("./pages/ConsultationIntake"));
const BookingComplete = lazy(() => import("./pages/BookingComplete"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AppDownload = lazy(() => import("./pages/AppDownload"));
const AmazonConsulting = lazy(() => import("./pages/AmazonConsulting"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const PerfilProfesional = lazy(() => import("./pages/PerfilProfesional"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/sobre-mi" element={<PageTransition><About /></PageTransition>} />
          <Route path="/servicios" element={<PageTransition><Services /></PageTransition>} />
          <Route path="/proyectos" element={<PageTransition><Projects /></PageTransition>} />
          <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
          <Route path="/blog/:slug" element={<PageTransition><BlogArticle /></PageTransition>} />
          <Route path="/contacto" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/aviso-legal" element={<PageTransition><LegalNotice /></PageTransition>} />
          <Route path="/politica-privacidad" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
          <Route path="/politica-cookies" element={<PageTransition><CookiePolicy /></PageTransition>} />
          <Route path="/reservar-consulta" element={<PageTransition><BookConsultation /></PageTransition>} />
          <Route path="/reserva-exitosa" element={<PageTransition><BookingSuccess /></PageTransition>} />
          <Route path="/formulario-consulta" element={<PageTransition><ConsultationIntake /></PageTransition>} />
          <Route path="/reserva-completada" element={<PageTransition><BookingComplete /></PageTransition>} />
          <Route path="/admin" element={<PageTransition><AdminPanel /></PageTransition>} />
          <Route path="/amazon-consulting" element={<PageTransition><AmazonConsulting /></PageTransition>} />
          <Route path="/portal" element={<PageTransition><ClientPortal /></PageTransition>} />
          <Route path="/app" element={<AppDownload />} />
          <Route path="/perfil-profesional" element={<PageTransition><PerfilProfesional /></PageTransition>} />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnimatedRoutes />
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
